import {useMessages} from "i18n";

/**
 * Content for the how-it-works surfaces (design `.hiw-modal`, `Transferify.dc.html`, ticket #45):
 * the two transfer lanes and the "At a glance" comparison. Pure data per epic #26 — the views
 * (`HowItWorksDialog`, `FeaturesContent`) render whatever the hooks return, so copy changes never
 * touch markup.
 *
 * Localised in ui#101: the lane/glance TEXT comes from the active catalog (`messages.features`),
 * while the STRUCTURE (lane ids/tones, which glance cells are emphasised or plain checks) stays here
 * as the single source of truth — so the yes/no shape can't drift between locales.
 */

export type LaneTone = "blue" | "teal";

export type HowItWorksLane = {
	id: "p2p" | "hosted";
	tone: LaneTone;
	/** Mono badge in the lane card (design `.hm-badge`). */
	badge: string;
	/** Ghost tag next to the badge (design `.hm-ghost`). */
	ghost: string;
	title: string;
	description: string;
	bullets: string[];
	/** The "Best for" footer value (design `.hm-foot`). */
	bestFor: string;
};

/** The two transfer lanes (P2P / hosted), in the active locale. */
export function useHowItWorksLanes(): HowItWorksLane[] {
	const {features} = useMessages();
	return [
		{id: "p2p", tone: "blue", ...features.lanes.p2p},
		{id: "hosted", tone: "teal", ...features.lanes.hosted},
	];
}

/** One side of an "At a glance" row: either a text (optionally emphasized) or a plain checkmark. */
export type GlanceCell = {text: string; strong?: boolean} | {check: true};

export type GlanceRow = {
	label: string;
	p2p: GlanceCell;
	hosted: GlanceCell;
};

/**
 * Design `.hm-cmp` rows, in the active locale. The emphasis (`strong`) and check/text shape per row
 * is fixed by the product; only the text comes from the catalog.
 */
export function useGlanceRows(): GlanceRow[] {
	const g = useMessages().features.glance;
	return [
		{label: g.linkReady.label, p2p: {text: g.linkReady.p2p, strong: true}, hosted: {text: g.linkReady.hosted}},
		{label: g.filePath.label, p2p: {text: g.filePath.p2p}, hosted: {text: g.filePath.hosted}},
		{label: g.speed.label, p2p: {text: g.speed.p2p, strong: true}, hosted: {text: g.speed.hosted}},
		{label: g.receiver.label, p2p: {text: g.receiver.p2p}, hosted: {text: g.receiver.hosted, strong: true}},
		{label: g.closingTab.label, p2p: {text: g.closingTab.p2p}, hosted: {text: g.closingTab.hosted, strong: true}},
		{label: g.whoCanReceive.label, p2p: {text: g.whoCanReceive.p2p}, hosted: {text: g.whoCanReceive.hosted, strong: true}},
		{label: g.filesStored.label, p2p: {text: g.filesStored.p2p, strong: true}, hosted: {text: g.filesStored.hosted}},
		{label: g.privacy.label, p2p: {text: g.privacy.p2p}, hosted: {text: g.privacy.hosted}},
		{label: g.fileSize.label, p2p: {text: g.fileSize.p2p, strong: true}, hosted: {text: g.fileSize.hosted}},
		{label: g.emailDelivery.label, p2p: {check: true}, hosted: {check: true}},
		{label: g.passwordProtect.label, p2p: {check: true}, hosted: {check: true}},
		{label: g.downloadLimit.label, p2p: {check: true}, hosted: {check: true}},
		{label: g.expiryControl.label, p2p: {text: g.expiryControl.p2p}, hosted: {check: true}},
		{label: g.readReceipt.label, p2p: {check: true}, hosted: {check: true}},
	];
}
